import { useEffect, useState } from "react";
import { CONFIG } from "../config";
import { Footer } from "../components/Footer";

/**
 * Les outils du moment — la page que le rapport ne peut pas être.
 *
 * ## Le compromis qu'elle résout
 *
 * Le rapport ne nomme aucune marque, et la consigne du prompt dit pourquoi :
 * « elles changent plus vite que ce rapport ». C'est juste — un document payant
 * qui citerait trois produits par leur nom serait daté avant d'être lu.
 *
 * Mais quelqu'un qui lit « utilisez un assistant conversationnel » doit encore
 * choisir, comparer, et découvrir seul ce qui est gratuit depuis un téléphone.
 * Le document gagnait en intemporalité ce que son lecteur perdait en
 * immédiateté, et c'est lui qui payait les deux.
 *
 * Cette page tient les deux bouts : le PDF reste sans marques et renvoie ici
 * par une adresse, qui ne se périme pas.
 *
 * ## Pourquoi la date est en évidence
 *
 * Une liste d'outils sans date est un piège : elle a l'air vraie
 * indéfiniment. Celle-ci porte la date de sa dernière révision, et la page dit
 * quoi en conclure quand elle est vieille. Même règle que partout ici — une
 * limite écrite vaut mieux qu'une précision supposée.
 */

interface LigneOutil {
  categorie: string;
  outils: string;
  note: string;
}

/**
 * Depuis combien de jours cette liste n'a-t-elle pas été revue ?
 *
 * En jours et non en mois : la page annonce une revue **mensuelle**, et un
 * décompte en mois ne saurait pas dire la différence entre « à jour » et « une
 * revue manquée ». La promesse et la mesure doivent avoir la même unité, sans
 * quoi la seconde ne peut pas contredire la première.
 */
function joursDepuis(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

/** Le seuil au-delà duquel la page dit elle-même que la cadence a dérapé. */
const JOURS_AVANT_ALERTE = 45;

export function Outils({ onSortir }: { onSortir: () => void }) {
  const [lignes, setLignes] = useState<LigneOutil[] | null>(null);
  const [majLe, setMajLe] = useState<string | null>(null);
  /*
    La liste vient-elle du code, ou l'éditeur l'a-t-il révisée ?

    Une liste d'outils porte une date, et une date engage à ce que quelqu'un
    ait regardé. Servir la liste de départ sous la même présentation qu'une
    liste révisée daterait une vérification qui n'a pas eu lieu — un faux
    signal au sens de la règle n° 6, sur la seule page du produit qui nomme
    des marques.
  */
  const [parDefaut, setParDefaut] = useState(false);

  useEffect(() => {
    let vivant = true;
    fetch(CONFIG.outilsEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: { lignes?: LigneOutil[]; majLe?: string | null; parDefaut?: boolean }) => {
        if (!vivant) return;
        setLignes(Array.isArray(corps.lignes) ? corps.lignes : []);
        setMajLe(corps.majLe ?? null);
        setParDefaut(corps.parDefaut === true);
      })
      .catch(() => {
        if (vivant) setLignes([]);
      });
    return () => {
      vivant = false;
    };
  }, []);

  const jours = majLe ? joursDepuis(majLe) : null;

  return (
    <>
      {/*
        L'ancre du lien d'évitement — 02/09/2026. Les autres pages hors parcours
        la portaient déjà ; celle-ci ne l'avait pas, et le lien de la barre n'y
        menait donc nulle part.
      */}
      <section className="band" id="contenu">
        <div className="shell narrow">
          <div className="kicker">Ressource</div>
          <h1>Les outils du moment</h1>
          <p className="lede">
            Votre rapport dit « un assistant conversationnel », jamais un nom de produit — parce que
            les marques changent plus vite qu'un document qu'on garde. Voici ce qu'elles sont
            aujourd'hui.
          </p>

          {/*
            La date, et ce qu'elle engage — 25/08/2026.

            Une liste d'outils sans date a l'air vraie indéfiniment. Celle-ci
            porte la sienne, et annonce la cadence à laquelle elle est revue.

            Annoncer une cadence est une promesse, donc un risque : si la revue
            dérape, la page continuerait de l'affirmer. C'est pourquoi la
            promesse est **falsifiable depuis la page elle-même** — la date
            affichée est celle de la dernière revue réellement passée, et au
            bout de quarante-cinq jours, soit une revue manquée et demie, la
            page le dit à la place du lecteur.
          */}
          {majLe !== null && (
            <p className="fine">
              <b>
                Revue le{" "}
                {new Date(majLe).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
                .
              </b>{" "}
              {jours !== null && jours > JOURS_AVANT_ALERTE ? (
                <>
                  Cette liste est revue le 15 de chaque mois, et celle-ci a pris du retard&nbsp;:
                  vérifiez avant de vous y fier, ce marché bouge vite.
                </>
              ) : (
                <>Cette liste est revue le 15 de chaque mois. La date ci-dessus est celle de la
                  dernière revue&nbsp;— si elle vieillit, c'est que la revue a pris du retard, et
                  vous le voyez.</>
              )}{" "}
              {parDefaut &&
                "Elle n'annonce aucun prix : un prix est faux quelques mois plus tard, et nous ne pouvons pas le vérifier à votre place."}
            </p>
          )}

          {lignes === null && <p className="fine">Chargement…</p>}

          {lignes !== null && lignes.length === 0 && (
            <p>
              Aucune liste n'est publiée pour le moment. Votre rapport reste utilisable tel quel :
              chaque action y décrit le geste, pas le produit.
            </p>
          )}

          {lignes !== null && lignes.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="tab">
                <thead>
                  <tr>
                    <th>Ce que dit votre rapport</th>
                    <th>Ce que c'est aujourd'hui</th>
                    <th>Ce qu'il faut savoir</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((ligne) => (
                    <tr key={`${ligne.categorie}-${ligne.outils}`}>
                      <td>
                        <b>{ligne.categorie}</b>
                      </td>
                      <td>{ligne.outils}</td>
                      <td className="fine">{ligne.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            Le désaveu compte autant que la liste. C'est le seul endroit du
            produit où l'on nomme des marques : y placer un lien rémunéré
            retournerait la règle n° 6 contre nous, à l'endroit exact où elle
            protège le plus.
          */}
          <p className="fine" style={{ marginTop: "18px" }}>
            Aucun de ces outils ne nous rémunère, aucun lien n'est affilié, et rien ici n'est
            classé « le meilleur ». Cette page dit ce qui existe et ce que ça coûte&nbsp;; le choix
            reste le vôtre.
          </p>

          <p style={{ marginTop: "20px" }}>
            <button type="button" className="btn btn-ghost" onClick={onSortir}>
              ← Revenir au diagnostic
            </button>
          </p>
        </div>
      </section>
      <Footer />
    </>
  );
}
